import { Effect, type Scope } from 'effect';
import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  attemptStudio,
  attemptStudioPromise,
  runStudioPromise,
  type StudioRuntimeError,
} from '../effect-runtime';
import { withStudioPageCanvasEffect } from './page-canvas';
import type { StudioContextValue, WorkspaceDoc } from './workspace';

type PreviewRenderer = Pick<StudioContextValue, 'renderPage' | 'getPageSize'>;
type PageSize = { width: number; height: number };

export const holdStudioPreviewCanvasEffect = Effect.fn('studio.preview.hold')(function* (
  host: HTMLDivElement,
  canvas: HTMLCanvasElement,
  ready: () => void,
  presentation: { className: string; width: string; height?: string } = {
    className: 'block w-full rounded-md border border-border bg-white',
    width: '100%',
  },
): Effect.fn.Return<never, StudioRuntimeError, Scope.Scope> {
  yield* Effect.acquireRelease(Effect.void, () =>
    attemptStudio('pdfjs', 'preview-unmount', () => {
      if (canvas.parentNode === host) canvas.remove();
    }).pipe(Effect.ignore),
  );
  yield* attemptStudio('pdfjs', 'preview-mount', () => {
    canvas.className = presentation.className;
    canvas.style.width = presentation.width;
    canvas.style.height = presentation.height ?? 'auto';
    host.replaceChildren(canvas);
    ready();
  });
  return yield* Effect.never;
}, Effect.scoped);

export const holdStudioPagePreviewEffect = Effect.fn('studio.preview.page')(function* (
  renderer: PreviewRenderer,
  page: number,
  host: HTMLDivElement,
  onSize: (size: PageSize) => void,
  ready: () => void,
) {
  const size = yield* attemptStudioPromise('pdfjs', 'preview-size', () =>
    renderer.getPageSize(page),
  );
  const scale = yield* attemptStudio('pdfjs', 'preview-scale', () => {
    onSize(size);
    return (host.clientWidth || 280) / size.width;
  });
  return yield* withStudioPageCanvasEffect(renderer.renderPage, page, scale, (canvas) =>
    holdStudioPreviewCanvasEffect(host, canvas, ready),
  );
});

/** The displayed preview stays scoped until its page, source document or panel closes. */
export function useStudioPagePreview(
  doc: WorkspaceDoc | null,
  page: number,
  hostRef: RefObject<HTMLDivElement | null>,
  { getPageSize, renderPage }: PreviewRenderer,
  onSize?: (size: PageSize) => void,
) {
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const sizeRef = useRef(onSize);
  useEffect(() => {
    sizeRef.current = onSize;
  });
  useEffect(() => {
    const host = hostRef.current;
    if (!doc || !host) {
      setRendering(false);
      setRenderError(null);
      return;
    }
    const controller = new AbortController();
    setRendering(true);
    setRenderError(null);
    void runStudioPromise(
      holdStudioPagePreviewEffect(
        { getPageSize, renderPage },
        page,
        host,
        (size) => {
          if (!controller.signal.aborted) sizeRef.current?.(size);
        },
        () => {
          if (!controller.signal.aborted) setRendering(false);
        },
      ),
      { signal: controller.signal },
    ).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setRenderError(cause instanceof Error ? cause.message : 'Preview failed');
      setRendering(false);
    });
    return () => controller.abort();
  }, [doc, page, hostRef, getPageSize, renderPage]);
  return { rendering, renderError };
}
